export enum LitterFunction {
  FOOD_DRINK = "food-drink",
  SMOKING = "smoking",
  PERSONAL_CARE = "personal-care",
  OTHER = "other"
}

export type LitterFunctionMeta = {
  label: string;
  icon: string;
};

// Definition of the litter functions, their display labels and icons.
// Used for rendering the UI and assigning categories to functions.
export const LITTER_FUNCTION_META: Record<LitterFunction, LitterFunctionMeta> =
  {
    [LitterFunction.FOOD_DRINK]: {
      label: "Food & Drink",
      icon: "RestaurantRounded"
    },
    [LitterFunction.SMOKING]: {
      label: "Smoking",
      icon: "SmokingRoomsRounded"
    },
    [LitterFunction.PERSONAL_CARE]: {
      label: "Personal Care",
      icon: "OpacityRounded"
    },
    [LitterFunction.OTHER]: {
      label: "Other",
      icon: "HelpOutlineRounded"
    }
  };

/**
 * Single source of truth for category function assignment and display order.
 * Categories not listed here default to OTHER and appear at the end.
 * NOTE: currently these need to be added to translations and custom categories JSON manually
 * Future improvement: allow to be fetched from Firestore db so can be updated without app release
 */
const ORDERED_CATEGORIES: Array<{ id: string; fn: LitterFunction }> = [
  // Food & Drink
  { id: "1003", fn: LitterFunction.FOOD_DRINK }, // Plastic bottle
  { id: "1050", fn: LitterFunction.FOOD_DRINK }, // Can
  { id: "1065", fn: LitterFunction.FOOD_DRINK }, // Glass bottle
  { id: "117", fn: LitterFunction.FOOD_DRINK }, // Drinking cup
  { id: "2000", fn: LitterFunction.FOOD_DRINK }, // Crisp packet
  { id: "1001", fn: LitterFunction.FOOD_DRINK }, // Plastic packaging
  { id: "NqgF6O6MBxOJlIbkLqIN", fn: LitterFunction.FOOD_DRINK }, // Compostable cup
  { id: "13", fn: LitterFunction.FOOD_DRINK }, // Plastic bottle lid
  { id: "116", fn: LitterFunction.FOOD_DRINK }, // Plastic straw
  { id: "118", fn: LitterFunction.FOOD_DRINK }, // Plastic cup lid
  { id: "132", fn: LitterFunction.FOOD_DRINK }, // Foam food container
  { id: "1016", fn: LitterFunction.FOOD_DRINK }, // Foam food container fragment
  { id: "2001", fn: LitterFunction.FOOD_DRINK }, // Chocolate wrapper
  { id: "2002", fn: LitterFunction.FOOD_DRINK }, // Sweet wrapper
  { id: "114", fn: LitterFunction.FOOD_DRINK }, // Plastic utensil / cutlery
  { id: "1048", fn: LitterFunction.FOOD_DRINK }, // Metal bottle cap
  { id: "aJm0G9r1KSSDnJW9x1Yj", fn: LitterFunction.FOOD_DRINK }, // Tetrapak
  { id: "2007", fn: LitterFunction.FOOD_DRINK }, // Plastic takeaway box

  // Smoking
  { id: "106", fn: LitterFunction.SMOKING }, // Cigarette butt
  { id: "104", fn: LitterFunction.SMOKING }, // Lighter
  { id: "owBCbFIBpMhoZtKLOuiO", fn: LitterFunction.SMOKING }, // Disposable vapes
  { id: "2008", fn: LitterFunction.SMOKING }, // Smoking packaging

  // Personal Care
  { id: "177", fn: LitterFunction.PERSONAL_CARE }, // Make up / cosmetics container
  { id: "135", fn: LitterFunction.PERSONAL_CARE }, // Sanitary towel
  { id: "2009", fn: LitterFunction.PERSONAL_CARE }, // Cotton bud
  { id: "1034", fn: LitterFunction.PERSONAL_CARE }, // Tampon
  { id: "1077", fn: LitterFunction.PERSONAL_CARE }, // Tampon applicator
  { id: "1037", fn: LitterFunction.PERSONAL_CARE }, // Wet wipe
  { id: "2010", fn: LitterFunction.PERSONAL_CARE }, // Toilet paper

  // Other
  { id: "100", fn: LitterFunction.OTHER }, // 4 / 6 pack rings
  { id: "1000", fn: LitterFunction.OTHER }, // Plastic bag
  { id: "129", fn: LitterFunction.OTHER }, // Plastic fragment
  { id: "1008", fn: LitterFunction.OTHER }, // Fishing equipment
  { id: "125", fn: LitterFunction.OTHER }, // Rope
  { id: "1067", fn: LitterFunction.OTHER }, // Glass fragment
  { id: "1091", fn: LitterFunction.OTHER }, // Cardboard
  { id: "178", fn: LitterFunction.OTHER }, // Toy
  { id: "GXscooK2PUGj3YsmNsAz", fn: LitterFunction.OTHER }, // Nos canister
  { id: "2003", fn: LitterFunction.OTHER }, // Face mask
  { id: "1092", fn: LitterFunction.OTHER }, // Paper packaging
  { id: "1043", fn: LitterFunction.OTHER }, // Footwear
  { id: "133", fn: LitterFunction.OTHER }, // Rubber
  { id: "197", fn: LitterFunction.OTHER }, // Electrical
  { id: "1042", fn: LitterFunction.OTHER }, // Clothing
  { id: "1020", fn: LitterFunction.OTHER }, // Balloon
  { id: "zfddHNpcCvvcfmOKUxzD", fn: LitterFunction.OTHER }, // Biodegradable packaging
  { id: "1057", fn: LitterFunction.OTHER }, // Metal fragment
  { id: "139", fn: LitterFunction.OTHER }, // Wood
  { id: "1059", fn: LitterFunction.OTHER }, // Cork
  { id: "1068", fn: LitterFunction.OTHER }, // Ceramic / pottery
  { id: "1085", fn: LitterFunction.OTHER }, // Bike
  { id: "2004", fn: LitterFunction.OTHER }, // Battery
  { id: "2005", fn: LitterFunction.OTHER }, // Disposable gloves
  { id: "2006", fn: LitterFunction.OTHER }, // Lollypop
  { id: "10003", fn: LitterFunction.OTHER }, // Tyre
  { id: "0kDic8cmdRcdFGBQ0ihE", fn: LitterFunction.OTHER }, // Plastic sachet
  { id: "5lMV1pEROlOyEXlPGItv", fn: LitterFunction.OTHER }, // Drinks pouch
  { id: "8BQxkJqGNPE92IbEXJyU", fn: LitterFunction.OTHER } // Aluminium foil
];

const CATEGORY_FUNCTION_MAP: Record<string, LitterFunction> =
  Object.fromEntries(ORDERED_CATEGORIES.map(({ id, fn }) => [id, fn]));

const CATEGORY_ORDER_INDEX = new Map(
  ORDERED_CATEGORIES.map(({ id }, i) => [id, i])
);

export function getCategoryFunction(categoryId: string): LitterFunction {
  return CATEGORY_FUNCTION_MAP[categoryId] ?? LitterFunction.OTHER;
}

export function getCategoriesForFunction(
  categoryJson: Record<string, { label: string; synonyms?: string[] }>,
  fn: LitterFunction
): Array<{ id: string; label: string; synonyms?: string[] }> {
  return Object.entries(categoryJson)
    .filter(([id]) => getCategoryFunction(id) === fn)
    .map(([id, { label, synonyms }]) => ({
      id,
      label,
      synonyms
    }))
    .sort((a, b) => {
      const ai = CATEGORY_ORDER_INDEX.get(a.id) ?? Infinity;
      const bi = CATEGORY_ORDER_INDEX.get(b.id) ?? Infinity;
      return ai - bi;
    });
}
